import React, { useRef, useState, useEffect } from "react";
import Icon from "@mdi/react";
import { mdiCameraOutline, mdiAccountCircleOutline } from "@mdi/js";
import { toast } from "react-toastify";
import webApiClient from "../../api/client";
import "./StudentAvatarUpload.css";

/**
 * StudentAvatarUpload — circular avatar editor for student profile photo.
 * Compresses to 512x512 JPEG (centre-crop) on file select and shows a preview.
 * When a photo already exists, clicking it calls onPreviewClick so the parent
 * can show a full-size preview modal before opening the file picker.
 * User confirms with Save or discards with Cancel. Remove button deletes the photo.
 *
 * // TODO: privacy disclosure — school-uploaded student photos are stored for
 * check-in identification only. See Terms & Conditions for full policy.
 */
const StudentAvatarUpload = ({
    currentImage,
    studentProfileId,
    onSaveSuccess,
    onSaveError,
    onRemove,
    onPreviewClick,
    forcePickerOpen,
}) => {
    const inputRef = useRef(null);
    const [previewBase64, setPreviewBase64] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // When parent increments forcePickerOpen (e.g. after "Change photo" in preview modal),
    // open the file picker directly regardless of whether a photo is displayed.
    useEffect(() => {
        if (forcePickerOpen) {
            inputRef.current?.click();
        }
    }, [forcePickerOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const compressImage = (file) => {
        return new Promise((resolve) => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const img = new Image();

            img.onload = () => {
                // Centre-crop to square, then resize to 512x512
                const size = Math.min(img.width, img.height);
                const sx = (img.width - size) / 2;
                const sy = (img.height - size) / 2;
                canvas.width = 512;
                canvas.height = 512;
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(0, 0, 512, 512);
                ctx.drawImage(img, sx, sy, size, size, 0, 0, 512, 512);
                resolve(canvas.toDataURL("image/jpeg", 0.85));
            };

            img.src = URL.createObjectURL(file);
        });
    };

    const handleAvatarClick = () => {
        if (currentImage && !isPreviewing && onPreviewClick) {
            // Has photo — delegate to parent to show preview modal first
            onPreviewClick();
            return;
        }
        if (!isSaving) inputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = "";

        const base64 = await compressImage(file);
        // TODO: open crop modal here before showing preview
        setPreviewBase64(base64);
    };

    const handleSave = async () => {
        if (!previewBase64) return;
        setIsSaving(true);
        // Strip data URL prefix — backend expects pure base64, not "data:image/jpeg;base64,..."
        const pureBase64 = previewBase64.includes(",") ? previewBase64.split(",")[1] : previewBase64;
        const response = await webApiClient.patch(
            `/api/v2/classes/students/${studentProfileId}/photo`,
            { base64Image: pureBase64 }
        );
        setIsSaving(false);
        if (response.ok) {
            toast.success("Student photo saved.");
            setPreviewBase64(null);
            if (onSaveSuccess) onSaveSuccess(previewBase64, response.data);
        } else {
            const msg = response.data?.message || "Failed to save photo. Please try again.";
            toast.error(msg);
            if (onSaveError) onSaveError(response);
        }
    };

    const handleCancel = () => {
        setPreviewBase64(null);
    };

    const handleRemove = async () => {
        setIsSaving(true);
        const response = await webApiClient.delete(
            `/api/v2/classes/students/${studentProfileId}/photo`
        );
        setIsSaving(false);
        if (response.ok) {
            toast.success("Photo removed.");
            if (onRemove) onRemove();
        } else {
            toast.error(response.data?.message || "Failed to remove photo.");
        }
    };

    const displayImage = previewBase64 || currentImage;
    const isPreviewing = !!previewBase64;

    return (
        <div className="student-avatar-upload">
            <button
                type="button"
                className={`avatar-btn${isPreviewing ? " is-previewing" : ""}`}
                onClick={handleAvatarClick}
                aria-label="Change student photo"
                disabled={isSaving}
            >
                {displayImage ? (
                    <img src={displayImage} alt="Student" className="avatar-img" />
                ) : (
                    <div className="avatar-placeholder">
                        <Icon path={mdiAccountCircleOutline} size={1.5} color="var(--dl-color-gray-500)" />
                    </div>
                )}

                {displayImage && !isPreviewing && (
                    <div className="avatar-overlay">
                        <Icon path={mdiCameraOutline} size={1.2} color="var(--dl-color-gray-white)" />
                    </div>
                )}

                {isSaving && <div className="avatar-spinner" />}
            </button>

            {isPreviewing ? (
                <div className="avatar-preview-actions">
                    <button
                        type="button"
                        className="avatar-save-btn"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? "Saving…" : "Save"}
                    </button>
                    <button
                        type="button"
                        className="avatar-cancel-btn"
                        onClick={handleCancel}
                        disabled={isSaving}
                    >
                        Cancel
                    </button>
                </div>
            ) : (
                <span className="avatar-hint">Click to add photo</span>
            )}

            {!isPreviewing && currentImage && onRemove && (
                <button
                    type="button"
                    className="student-avatar-remove-btn"
                    onClick={handleRemove}
                    disabled={isSaving}
                >
                    Remove photo
                </button>
            )}

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
        </div>
    );
};

export default StudentAvatarUpload;
